import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Clock,
  Mail,
  ExternalLink,
  Bell,
  HandHeart,
  Youtube,
  CalendarDays,
} from "lucide-react";
import api from "../lib/api";
import { extractApiError } from "../lib/errors";
import MinistryGrid from "../components/MinistryGrid";
import { CHURCH_MINISTRIES } from "../lib/ministries";
import {
  LOGO_FATHERS_HOUSE,
  CHURCH_NAME,
  CHURCH_TAGLINE,
  CHURCH_ADDRESS,
  CHURCH_SERVICE_TIME,
  CONTACT_WHATSAPP,
  LINK_CHURCH_YT,
  MAP_LINK_CHURCH,
} from "../lib/siteConfig";

const CHURCH_IMG =
  "https://customer-assets.emergentagent.com/job_life-talk-radio/artifacts/dvirzgmn_Church%20Hero.jpg";

// Tauranga, Gate Pa approximate coords for OpenStreetMap embed
// Google Maps embed — points to the church's verified address.
// `q=` accepts a plain query string; output=embed renders the dark-themed
// branded Google map. Click-through still lands on Google Maps for routing.
const GOOGLE_MAP_EMBED =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3157.495567507141!2d176.16540759999998!3d-37.6845591!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6d6ddbdd316ec7f5%3A0xc64c34276d873284!2sSaint%20Peters%20In%20The%20City!5e0!3m2!1sen!2sin!4v1790787478692!5m2!1sen!2sin";
const OSM_LINK = MAP_LINK_CHURCH;

export default function Church() {
  return (
    <main data-testid="church-page" className="bg-canvas">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage: `url(${CHURCH_IMG})`,
            backgroundSize: "cover",
            backgroundPosition: "center 30%",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(11,12,16,0.45) 0%, rgba(11,12,16,0.78) 60%, rgba(11,12,16,0.96) 88%, #0B0C10 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16">
          <div className="flex items-center gap-4 mb-6">
            <span className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-white p-1.5 ring-1 ring-[#C5D1EB]/25">
              <img
                src={LOGO_FATHERS_HOUSE}
                alt={CHURCH_NAME}
                className="w-full h-full object-contain"
              />
            </span>
            <div data-testid="church-hero-brand">
              <p className="text-[11px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                {CHURCH_NAME}
              </p>
              <p className="mt-1 font-display text-white text-sm tracking-tight">
                {CHURCH_TAGLINE}
              </p>
            </div>
          </div>
          <h1 className="mt-4 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            A house of prayer for all nations — in Tauranga.
          </h1>
          <p className="mt-5 text-[#C5D1EB] max-w-2xl leading-relaxed">
            We gather weekly to worship in Spirit and truth, to pray for our
            city, and to serve the Indian and multi-ethnic community of the Bay
            of Plenty.
          </p>
        </div>
      </section>

      {/* Visit panel */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 flex flex-col gap-5">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                Visit
              </p>
              <p className="mt-3 text-white text-base leading-relaxed flex items-start gap-2">
                <MapPin size={16} className="mt-1 text-[#C5001A]" />
                {CHURCH_ADDRESS}
              </p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                Call / WhatsApp
              </p>
              <p className="mt-3 text-white text-base flex items-center gap-2">
                <Phone size={16} className="text-[#C5001A]" /> {CONTACT_WHATSAPP}
              </p>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5D1EB]/70">
                Sunday Service
              </p>
              <p className="mt-3 text-white text-base flex items-center gap-2">
                <Clock size={16} className="text-[#C5001A]" /> {CHURCH_SERVICE_TIME.replace("Every Sunday · ", "")}
              </p>
            </div>
            <a
              href={OSM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="church-directions"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-5 py-3 text-sm"
            >
              <MapPin size={14} /> Get Directions
            </a>
            <Link
              to="/contact#prayer"
              data-testid="church-prayer-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-[#C5001A] text-white px-5 py-3 text-sm transition-colors"
            >
              <HandHeart size={14} /> Make a Prayer Request
            </Link>
          </div>

          <a
            href={OSM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="church-map-link"
            className="lg:col-span-2 group relative bg-white/[0.04] backdrop-blur-md border border-white/10 p-2 min-h-[420px] flex overflow-hidden"
          >
            <iframe
              data-testid="church-map"
              title="Father's House Tauranga — Google Maps"
              src={GOOGLE_MAP_EMBED}
              className="w-full h-full min-h-[420px] pointer-events-none"
              style={{
                border: 0,
                // Invert + hue-rotate is the standard CSS recipe for putting
                // Google Maps into a dark/luxury palette without an API key.
                filter:
                  "invert(0.92) hue-rotate(180deg) saturate(0.75) contrast(0.95) brightness(0.95)",
              }}
              loading="lazy"
            />
            <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-[#0B0C10]/90 text-white text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border border-[#C5001A]/40 group-hover:border-[#C5001A] transition-colors">
              <MapPin size={11} /> Open in Google Maps
            </span>
          </a>
        </div>
      </section>

      {/* Weekly Rhythm — Sunday Service highlight (moved to top) */}
      <SundayHighlight />

      {/* Father's House ministries (moved from Home) */}
      <MinistryGrid
        items={CHURCH_MINISTRIES}
        heading="How we serve our city."
        subheading="Six ministries through which the Father's House family lives out the Gospel."
        testId="church-ministries"
      />

      {/* Stay in touch */}
      <StayInTouch />

      {/* YouTube CTA — replaces full sermon list */}
      <YouTubeCta />
    </main>
  );
}

/* -------------------------------------------------------------------------- */
/*  Weekly Rhythm — Sunday Service highlight                                   */
/* -------------------------------------------------------------------------- */
function SundayHighlight() {
  return (
    <section
      data-testid="sunday-highlight"
      className="relative overflow-hidden border-t border-white/5 bg-[#0B0C10]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-24 w-[36rem] h-[36rem] rounded-full opacity-25 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-20">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
          Weekly Rhythm
        </span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-3 tracking-tight">
          When we gather.
        </h2>

        <div className="mt-8 grid lg:grid-cols-[1fr_auto] items-stretch gap-4 border border-white/10 bg-white/[0.04] backdrop-blur-md">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8 px-6 sm:px-8 py-6">
            <span className="inline-flex items-center justify-center w-14 h-14 bg-[#C5001A]/15 border border-[#C5001A]/60 text-[#C5001A]">
              <CalendarDays size={20} />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5001A]">
                Sunday Service
              </p>
              <p className="mt-1 font-display text-white text-xl sm:text-2xl tracking-tight">
                Worship + Word — every Sunday
              </p>
              <p className="mt-1 text-sm text-[#C5D1EB] flex items-center gap-2">
                <Clock size={13} className="text-[#C5001A]" />
                4:00 PM – 6:00 PM · {CHURCH_ADDRESS}
              </p>
            </div>
          </div>
          <Link
            to="/church#church-newsletter"
            data-testid="sunday-stay-in-touch"
            className="group inline-flex items-center justify-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] text-white px-6 sm:px-8 py-5 text-sm font-medium tracking-tight lg:rounded-none transition-colors"
          >
            <Bell size={15} /> Stay in touch for upcoming events
            <ExternalLink
              size={12}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  YouTube CTA — replaces the long sermon list                               */
/* -------------------------------------------------------------------------- */
function YouTubeCta() {
  return (
    <section
      data-testid="church-youtube-cta"
      className="relative overflow-hidden border-t border-white/5 bg-[#0B0C10]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, rgba(197,0,26,0.32), transparent 55%), radial-gradient(circle at 75% 70%, rgba(227,28,61,0.22), transparent 55%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
          Sermons & Worship
        </span>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl mx-auto leading-[1.05]">
          Watch every sermon, worship set & testimony on YouTube.
        </h2>
        <p className="mt-5 text-[#C5D1EB] max-w-2xl mx-auto leading-relaxed">
          Our full library of Sunday messages, Hindi & multi-language worship,
          prayer nights, and stories of breakthrough lives on the official
          Father's House Tauranga channel — updated every week.
        </p>
        <a
          href={LINK_CHURCH_YT}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="church-youtube-cta-btn"
          className="group mt-9 inline-flex items-center gap-3 bg-[#FF0033] hover:bg-[#E31C3D] text-white px-8 sm:px-10 py-4 sm:py-5 text-sm sm:text-base font-medium tracking-tight shadow-[0_10px_50px_rgba(255,0,51,0.4)] transition-all hover:-translate-y-0.5"
        >
          <Youtube size={20} className="shrink-0" />
          Open the Father's House YouTube Channel
          <ExternalLink
            size={14}
            className="opacity-70 group-hover:opacity-100 transition-opacity"
          />
        </a>
        <p className="mt-5 text-xs tracking-[0.25em] uppercase text-[#C5D1EB]/60">
          New uploads every Sunday · Hindi · English
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stay-in-touch newsletter signup                                            */
/* -------------------------------------------------------------------------- */
function StayInTouch() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error("That email doesn't look right — please check.");
      return;
    }
    try {
      setLoading(true);
      await api.post("/newsletter", {
        name: name.trim(),
        email: email.trim(),
        source: "church_stay_in_touch",
      });
      toast.success("You're on the list — we'll keep you posted.");
      setName("");
      setEmail("");
    } catch (er) {
      toast.error(extractApiError(er, "Could not subscribe right now."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="church-newsletter"
      data-testid="church-newsletter"
      className="relative overflow-hidden border-t border-white/5"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-32 w-[40rem] h-[40rem] rounded-full opacity-25 blur-[120px]"
        style={{ background: "radial-gradient(circle, #C5001A, transparent 70%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-6">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#C5D1EB]/70">
              Stay in touch
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
              Hear about upcoming events first.
            </h2>
            <p className="mt-5 text-[#C5D1EB] leading-relaxed max-w-xl">
              Conferences, prayer nights, family days, baptisms, and special
              broadcasts — we'll let you know when something is coming up at
              Father's House Tauranga.
            </p>
          </div>

          <form
            data-testid="church-newsletter-form"
            onSubmit={submit}
            className="lg:col-span-6 bg-white/[0.04] backdrop-blur-md border border-white/10 p-7 sm:p-9"
          >
            <div className="flex items-center gap-3">
              <Bell size={18} className="text-[#C5001A]" />
              <p className="font-display text-white text-lg tracking-tight">
                Subscribe for event updates
              </p>
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Name
                </label>
                <input
                  data-testid="newsletter-name"
                  className="brand-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-[#C5D1EB]/70 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  data-testid="newsletter-email"
                  className="brand-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <button
              data-testid="newsletter-submit"
              disabled={loading}
              className="mt-5 inline-flex items-center gap-2 bg-[#C5001A] hover:bg-[#E31C3D] disabled:opacity-60 text-white px-6 py-3 text-sm transition-colors"
            >
              <Mail size={14} /> {loading ? "Sending…" : "Keep me updated"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

